import { useEffect, useState } from 'react';
import {
  getLastFlightSearch,
  getRecentFlightSearches,
  getLastHotelSearch,
  getRecentHotelSearches,
  clearSearches,
  getLastOffer,
  getRecentOffers
} from '../../utils/userSearchStorage';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export default function AdminSearchDebugDialog({ open, onClose = () => { } }) {
  const [data, setData] = useState({
    lastFlight: null,
    recentFlights: [],
    lastHotel: null,
    recentHotels: [],
    lastOffer: null,
    recentOffers: []
  });
  const [isOpened, setIsOpened] = useState(open);

  useEffect(() => {
    if (!open) return;
    setData({
      lastFlight: getLastFlightSearch(),
      recentFlights: getRecentFlightSearches(),
      lastHotel: getLastHotelSearch(),
      recentHotels: getRecentHotelSearches(),
      lastOffer: getLastOffer(),
      recentOffers: getRecentOffers()
    });
  }, [open]);

  const renderObjectAsString = (obj) => {
    if (typeof obj === 'object') {
      return JSON.stringify(obj, null, 2);
    }
    return obj;
  };

  const renderSearchTable = (searches, type = 'flight') => {
    const headers = (() => {
      switch (type) {
        case 'flight':
          return ['Created At', 'Origin', 'Destination', 'Departure', 'Return', 'Type', 'Passengers', 'Expires At'];
        case 'hotel':
          return ['Created At', 'Location', 'Country', 'Check-in', 'Check-out', 'Guests', 'Rooms', 'Expires At'];
        case 'offer':
          return ['Created At', 'Origin', 'Destination', 'Departure', 'Return', 'Class', 'Airline', 'Price', 'Expires At'];
        default:
          throw new Error(`Unknown type '${type}'`);
      }
    })();

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers.map(header => (
              <TableCell key={header}><strong>{header}</strong></TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {searches.map((search, index) => (
            <TableRow key={index}>
              {type === 'flight' && (
                <>
                  <TableCell>{search.createdAt.split('T')[0]}</TableCell>
                  <TableCell>{search.origin.city} ({search.origin.code})</TableCell>
                  <TableCell>{search.destination.city} ({search.destination.code})</TableCell>
                  <TableCell>{search.departureDate}</TableCell>
                  <TableCell>{search.returnDate || '-'}</TableCell>
                  <TableCell>{search.flightType}</TableCell>
                  <TableCell>{renderObjectAsString(search.passengers)}</TableCell>
                  <TableCell>{search.expiresAt.split('T')[0]}</TableCell>
                </>
              )}
              {type === 'hotel' && (
                <>
                  <TableCell>{search.createdAt.split('T')[0]}</TableCell>
                  <TableCell>{search.destination}</TableCell>
                  <TableCell>{search.destinationCountry}</TableCell>
                  <TableCell>{search.departure}</TableCell>
                  <TableCell>{search.return}</TableCell>
                  <TableCell>{renderObjectAsString(search.guestsCount)}</TableCell>
                  <TableCell>{search.rooms}</TableCell>
                  <TableCell>{search.expiresAt.split('T')[0]}</TableCell>
                </>
              )}

              {type === 'offer' && (
                <>
                  <TableCell>{search.createdAt.split('T')[0]}</TableCell>
                  <TableCell>{search.origin?.name} ({search.origin?.code})</TableCell>
                  <TableCell>{search.destination?.name} ({search.destination?.code})</TableCell>
                  <TableCell>{search.departureDate}</TableCell>
                  <TableCell>{search.returnDate}</TableCell>
                  <TableCell>{search.cabinClass}</TableCell>
                  <TableCell>{search.airline}</TableCell>
                  <TableCell>{search.price} {search.currency}</TableCell>
                  <TableCell>{search.expiresAt.split('T')[0]}</TableCell>
                </>
              )}

            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Dialog
      open={isOpened}
      onClose={() => {
        setIsOpened(false);
        onClose();
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        Admin Search Debug
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Last Flight Search</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {data.lastFlight ? (
              <Typography variant="body2" component="div">
                <strong>{data.lastFlight.origin.city}</strong> → <strong>{data.lastFlight.destination.city}</strong><br />
                {data.lastFlight.departureDate} to {data.lastFlight.returnDate || 'N/A'}<br />
                {data.lastFlight.flightType},
                {/* {data.lastFlight.passengers} pax */}
                {data.lastFlight.adults && (
                  <div>
                    <strong>Adults:</strong> {renderObjectAsString(data.lastFlight.adults)}
                  </div>
                )}
                {data.lastFlight.childrenAges && (
                  <div>
                    <strong>Children Ages:</strong> {renderObjectAsString(data.lastFlight.childrenAges)}
                  </div>
                )}
              </Typography>
            ) : <Typography>No flight data</Typography>}
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Recent Flight Searches</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {data.recentFlights.length > 0
              ? renderSearchTable(data.recentFlights, 'flight')
              : <Typography>No recent flights</Typography>}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Last Hotel Search</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {data.lastHotel ? (
              <Typography variant="body2" component="div">
                <strong>{data.lastHotel.destination}</strong><br />
                {data.lastHotel.departure} to {data.lastHotel.return}<br />
                {data.lastHotel.guestsCount.numGuests} guest(s), {data.lastHotel.rooms} room(s)
              </Typography>
            ) : <Typography>No hotel data</Typography>}
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Recent Hotel Searches</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {data.recentHotels.length > 0
              ? renderSearchTable(data.recentHotels, 'hotel')
              : <Typography>No recent hotels</Typography>}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Last Offer Search</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {data.lastOffer ? (
              <Typography variant="body2" component="div">
                <strong>{data.lastOffer.origin.name}</strong> → <strong>{data.lastOffer.destination.name}</strong><br />
                {data.lastOffer.departureDate} to {data.lastOffer.returnDate || 'N/A'}<br />
                {data.lastOffer.cabinClass}, {data.lastOffer.airline}, {data.lastOffer.price} {data.lastOffer.currency}
              </Typography>
            ) : <Typography>No offer data</Typography>}
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Recent Offer Searches</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {data.recentOffers.length > 0
              ? renderSearchTable(data.recentOffers, 'offer')
              : <Typography>No recent offers</Typography>}
          </AccordionDetails>
        </Accordion>

        <div>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              clearSearches();
              setIsOpened(false);
              onClose();
            }}
            className="mt-4 mr-4"
          >
            Clear All Search Data
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setIsOpened(false);
              onClose();
            }}
            className="mt-4"
          >
            Close
          </Button>
        </div>
      </DialogContent>

    </Dialog>
  );
}